import { useState } from "react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useAddManualAdjustment, useLeagueParticipants, useManualAdjustments } from "@/modules/leagues/hooks/useLeagueAdmin";
import { SettingsSubHeader } from "@/modules/leagues/components/SettingsSubHeader";
import { Label } from "@/components/atoms/Label";
import { Input } from "@/components/atoms/Input";
import { Textarea } from "@/components/atoms/Textarea";
import { Button } from "@/components/atoms/Button";
import { Badge } from "@/components/atoms/Badge";
import { useToast } from "@/hooks/use-toast";

export function LeagueSettingsImportPointsPage() {
  const { league } = useLeagueContext();
  const { toast } = useToast();
  const { data: participants } = useLeagueParticipants(league.id);
  const { data: adjustments, isLoading: loadingHistory } = useManualAdjustments(league.id);
  const addAdjustment = useAddManualAdjustment(league.id);

  const [participantUid, setParticipantUid] = useState("");
  const [pontos, setPontos] = useState(0);
  const [motivo, setMotivo] = useState("");

  const canSubmit = participantUid !== "" && pontos !== 0 && motivo.trim().length > 0;

  function handleSubmit() {
    if (!canSubmit) return;
    addAdjustment.mutate(
      { participantUid, pontos, motivo: motivo.trim() },
      {
        onSuccess: () => {
          toast({ title: "Pontos importados", description: "O ranking já reflete o ajuste." });
          setParticipantUid("");
          setPontos(0);
          setMotivo("");
        },
        onError: (err) => toast({ title: "Não foi possível salvar", description: err.message, variant: "destructive" }),
      }
    );
  }

  return (
    <div className="space-y-5">
      <SettingsSubHeader leagueId={league.id} title="Importar Pontos" />
      <p className="text-xs text-muted-foreground">
        Adicione ou remova pontos manualmente de um participante. O motivo é obrigatório e tudo fica registrado no
        Histórico de Pontuação.
      </p>

      <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
        <div className="space-y-1.5">
          <Label htmlFor="participant">Participante</Label>
          <select
            id="participant"
            value={participantUid}
            onChange={(e) => setParticipantUid(e.target.value)}
            className="flex h-12 w-full rounded-xl border border-input bg-surface px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Selecione...</option>
            {participants?.map((p) => (
              <option key={p.uid} value={p.uid}>
                {p.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pontos">Pontos (negativo para desconto)</Label>
          <Input id="pontos" type="number" value={pontos} onChange={(e) => setPontos(Number(e.target.value))} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="motivo">Motivo (obrigatório)</Label>
          <Textarea id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex: acertou a pergunta bônus da rodada 14" />
        </div>

        <Button className="w-full" disabled={!canSubmit} isLoading={addAdjustment.isPending} onClick={handleSubmit}>
          Aplicar ajuste
        </Button>
      </div>

      <div className="space-y-2">
        <h2 className="font-display text-sm font-semibold">Ajustes anteriores</h2>
        {loadingHistory && <p className="text-xs text-muted-foreground">Carregando...</p>}
        {adjustments?.length === 0 && <p className="text-xs text-muted-foreground">Nenhum ajuste manual ainda.</p>}
        {adjustments?.map((adj) => (
          <div key={adj.id} className="flex items-start justify-between gap-2 rounded-xl border border-border bg-card p-3">
            <div>
              <p className="text-sm font-medium">{adj.participantNome}</p>
              <p className="text-xs text-muted-foreground">{adj.motivo}</p>
            </div>
            <Badge variant={adj.pontos >= 0 ? "success" : "destructive"}>
              {adj.pontos >= 0 ? "+" : ""}
              {adj.pontos} pts
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
